/**
 * @file UserAvatar.jsx
 * @description A flexible, fallback-aware avatar component that displays a user's Google profile 
 *              picture, their initial on a gradient, or a default icon.
 * @module components/UserAvatar
 */
import React from 'react';
import { FiUser } from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component UserAvatar
 * @description Renders a user's avatar image with multiple graceful fallbacks (initial or icon).
 *
 * @param {Object} props
 * @param {string} [props.avatarUrl] - URL of the user's avatar (e.g., from Google OAuth metadata).
 * @param {string} [props.name] - User's name or username to extract an initial from if no avatarUrl.
 * @param {number} [props.size=24] - Size of the avatar in pixels.
 * @param {string} [props.className=''] - Additional CSS classes for the wrapper.
 * @returns {JSX.Element} The rendered UserAvatar.
 */
const UserAvatar = ({ avatarUrl, name, size = 24, className = '' }) => {
    /**
     * @function renderFallback
     * @description Renders a fallback UI (either a gradient circle with the first initial or a default user icon).
     * @returns {JSX.Element}
     */
    const renderFallback = () => {
        if (name) {
            const initial = name.charAt(0).toUpperCase();
            return (
                <div
                    className={`flex items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 text-white font-semibold flex-shrink-0 ${className}`}
                    style={{ width: size, height: size, fontSize: size * 0.45 }}
                >
                    {initial}
                </div>
            );
        }
        return (
            <div
                className={`flex items-center justify-center rounded-full bg-zinc-800 flex-shrink-0 ${className}`}
                style={{ width: size, height: size }}
            >
                <FiUser className="text-gray-400" style={{ width: size * 0.6, height: size * 0.6 }} />
            </div>
        );
    };

    if (avatarUrl) {
        return (
            <div className="relative inline-flex flex-shrink-0">
                <img
                    src={avatarUrl}
                    alt={name || "Profile"}
                    className={`rounded-full object-cover flex-shrink-0 ${className}`}
                    style={{ width: size, height: size }}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                        // If image fails to load, replace with fallback
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                        }
                    }}
                />
                <div style={{ display: 'none' }} className="absolute inset-0">
                    {renderFallback()}
                </div>
            </div>
        );
    }

    return renderFallback();
};

export default UserAvatar;
